import React, { useState, useEffect } from 'react';
import useAppwrite from '../../../lib/useAppwrite';
import { config, deleteDocument, getAllDocuments, getCurrentUser } from '../../../lib/appwrite';
import { useStateContext } from '../../../contexts/ContextProvider';
import { FaCirclePlus, FaTrashCan } from "react-icons/fa6";
import { FaRegEdit } from "react-icons/fa";
import EditTestimonial from './EditTestimonial';
import ConfirmDelete from '../../../components/ConfirmDelete';

const Testimonials = () => {
  const { data: user } = useAppwrite(() => getCurrentUser());
  const { currentColor } = useStateContext();
  const [createRecord, setCreateRecord] = useState(false);
  const [editRecord, setEditRecord] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  // Data
  const { data, isLoading, refetch } = useAppwrite(
    () => getAllDocuments(config.testimonialCollectionId)
  );

  useEffect(() => {
    if (data) {
      setTestimonials(data);
    }
  }, [data]);

  useEffect(() => {
    if (editRecord === false && createRecord === false) {
      refetch();
    }
  }, [editRecord, createRecord]);

  const handleRowSelect = (testimonial) => {
    setSelectedRowData(testimonial);
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    await deleteDocument(selectedRowData.$id || selectedRowData.id, config.testimonialCollectionId);
    refetch();
    setSelectedRowData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'Denied':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'Waiting for Review':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Testimonials</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage customer reviews and testimonials
            </p>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedRowData(null);
              setCreateRecord(true);
              console.log('Create button clicked, createRecord set to:', true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
          >
            <FaCirclePlus />
            <span>Add Testimonial</span>
          </button>
        </div>
      </div>

      {/* Testimonials Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading testimonials...</p>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">No testimonials found.</p>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedRowData(null);
                setCreateRecord(true);
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Add your first testimonial
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Review
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {testimonials.map((testimonial, index) => (
                  <tr
                    key={testimonial.id || testimonial.$id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                      selectedRowData?.id === testimonial.id || selectedRowData?.$id === testimonial.$id
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : ''
                    }`}
                    onClick={() => handleRowSelect(testimonial)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {testimonial.firstName} {testimonial.lastInitial}.
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 max-w-md">
                        {testimonial.review}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          testimonial.status
                        )}`}
                      >
                        {testimonial.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {testimonial.created_at
                        ? new Date(testimonial.created_at).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(testimonial);
                            setEditRecord(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          title="Edit"
                        >
                          <FaRegEdit />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedRowData(testimonial);
                            setConfirmDelete(true);
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <FaTrashCan />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      {createRecord && (
        <EditTestimonial
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={null}
          setSelectedRowData={setSelectedRowData}
          mode="create"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}
      {editRecord && (
        <EditTestimonial
          view={true}
          setView={(val) => {
            setCreateRecord(false);
            setEditRecord(false);
          }}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          mode="edit"
          onSuccess={() => {
            setCreateRecord(false);
            setEditRecord(false);
            setSelectedRowData(null);
            refetch();
          }}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDelete
        title="Confirm Deletion?"
        text="Are you sure you want to delete this testimonial? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        visible={confirmDelete}
        setVisible={setConfirmDelete}
        handleConfirm={handleDelete}
      />
    </div>
  );
};

export default Testimonials;
